import { useEffect, useMemo, useRef, useState } from "react";
import type {
  CopilotBlock,
  CopilotBlockingReading,
  CopilotChip,
  CopilotChoice,
  CopilotFactSheet,
  CopilotFinding,
  CopilotKept,
  CopilotLastRun,
  CopilotMessage,
  CopilotObservation,
  CopilotPatch,
  CopilotPatchOp,
  CopilotPick,
  CopilotReadout,
  CopilotReviewedSetting,
  CopilotRuleChange,
  CopilotRunState,
  CopilotSettingRow,
  CopilotSetupRow,
  CopilotSuggestion,
  ResponsePostConfigCopilotBrief,
  ResponsePostConfigCopilotTurn,
} from "@CommonApi/merchflows/_merchflowId_/config-copilot";
import type { PspRules } from "@CommonTypes/backend/PspRules";
import { useTranslation } from "src/i18n";
import {
  OUnavailableError,
  postConfigCopilotBrief,
  postConfigCopilotOpening,
  postConfigCopilotReadout,
  postConfigCopilotTurn,
} from "./api";
import {
  changedPaths,
  holds,
  isSameValue,
  patchedRules,
  undoOps,
  valueAt,
  presentAt,
  absentParents,
} from "./rulesPatch";
import { sketchPaletteOf } from "./sketchPalette";

export interface CopilotEntry extends CopilotMessage {
  id: number;

  applied?: { patch: CopilotPatch; raises: CopilotFinding[]; changes?: CopilotRuleChange[] };
}

export type CopilotAsk = { text: string } | { pick: CopilotPick; shown: string };

export interface CopilotTrouble {
  line: string;
  retry?: () => void;
}

type CopilotOption = CopilotChoice["options"][number];

export interface CopilotPendingFix {
  finding?: string;

  adjust?: boolean;

  run?: number;

  onTaken: () => void;
}

interface Options {
  merchflowId: number;
  basePogId: number;
  blocking: CopilotBlockingReading | null;

  readRules: () => PspRules;
  writeRules: (rules: PspRules) => void;
  pendingFix?: CopilotPendingFix;
  onGenerate?: () => void;
}

export interface CopilotIdleSwitch {
  id: string;
  label: string;
  reason: string;

  after: string;
}

const BRAND_BLOCKS = { id: "brand_blocks", path: "high_density_mode.brand_blocks_min_bays" };
const BLOCKING_CHOICE = "blocking";

// A complete Advanced walkthrough can have more than ten no-change answers.
const HISTORY_TEXT_TURNS = 128;
const HISTORY_TOTAL_TURNS = 256;
const HISTORY_TEXT_MAX = 2000;

export const BRIEF_TIMEOUT_MS = 10000;

/** The model's opening takes 2.5–4.7 s; past this the brief's own words stay. */
export const OPENING_TIMEOUT_MS = 10000;

// Every brief that carries rules for the screen; "screen" means the page's own stay.
const SERVED_FROM: ReadonlySet<ResponsePostConfigCopilotBrief["rulesFrom"]> = new Set([
  "last_run",
  "o_picks",
  "o_setup",
]);

const SETUP_PATCH = "o_setup";

export const MAX_ASKS = 3;

export type SetupRowState = "on" | "off" | "moved";

export const THINKING_AFTER_MS = 300;

class TurnNotSent extends Error {}

const withinTime = <T>(work: Promise<T>, ms: number) =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("timed out")), ms);
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error instanceof Error ? error : new Error(String(error)));
      },
    );
  });

const textOf = (blocks: readonly CopilotBlock[]) =>
  blocks
    .map((block) => {
      if (block.type === "text") return block.text;
      if (block.type === "patch") return `Offered: ${block.patch.title}.`;
      return "";
    })
    .filter(Boolean)
    .join(" ");

// Keep every applied change in history so typed Undo can reach the oldest one.
const historyOf = (entries: readonly CopilotEntry[]) => {
  const recent = new Set(
    entries
      .filter((entry) => !entry.applied)
      .slice(-HISTORY_TEXT_TURNS)
      .map((entry) => entry.id),
  );
  return entries
    .filter((entry) => entry.applied || recent.has(entry.id))
    .slice(-HISTORY_TOTAL_TURNS)
    .map((entry) => ({
      role: entry.role,
      text: textOf(entry.blocks).slice(0, HISTORY_TEXT_MAX),
      ...(entry.applied?.changes?.length ? { changes: entry.applied.changes } : {}),
    }));
};

interface MadeChange {
  id: number;
  paths: string[];
  before: PspRules;
  after: PspRules;

  strip: CopilotRuleChange[];
}

export const overlaps = (a: string, b: string) =>
  a === b || a.startsWith(`${b}.`) || b.startsWith(`${a}.`);

const rowsOf = (made: MadeChange, strip: readonly CopilotRuleChange[]) => {
  const touches = (row: CopilotRuleChange) =>
    row.paths.some((path) => made.paths.some((one) => overlaps(path, one)));
  const values = (row: CopilotRuleChange) => ({
    from: row.paths.map((path) => valueAt(made.before, path) ?? null),
    to: row.paths.map((path) => valueAt(made.after, path) ?? null),
    fromPresent: row.paths.map((path) => presentAt(made.before, path)),
    toPresent: row.paths.map((path) => presentAt(made.after, path)),
    fromAbsentParents: row.paths.map((path) => absentParents(made.before, path)),
    toAbsentParents: row.paths.map((path) => absentParents(made.after, path)),
  });
  const was = new Map(made.strip.map((row) => [changeKey(row), row]));
  const now = strip.filter(touches);
  const kept = new Set(now.map(changeKey));
  return [
    ...now.map((row) => ({
      ...row,
      fromText: was.get(changeKey(row))?.toText ?? row.fromText,
      ...values(row),
    })),
    ...made.strip
      .filter((row) => touches(row) && !kept.has(changeKey(row)))
      .map((row) => ({ ...row, fromText: row.toText, toText: row.fromText, ...values(row) })),
  ].filter((row) =>
    row.from.some(
      (from, index) =>
        row.fromPresent[index] !== row.toPresent[index] || !isSameValue(from, row.to[index]),
    ),
  );
};

// A change the page made itself, journalled like one of O's.
const pagePatch = (id: string, title: string, ops: CopilotPatchOp[]): CopilotPatch => ({
  id,
  title,
  why: "",
  expect: "",
  cost: "",
  evidence: "untested",
  ops,
});

type PatchBlock = Extract<CopilotBlock, { type: "patch" }>;

const singlePatchOf = (blocks: readonly CopilotBlock[]): PatchBlock | null => {
  const patches = blocks.flatMap((block) => (block.type === "patch" ? [block] : []));
  return patches.length === 1 ? patches[0] : null;
};

/**
 * Chips offered beside a reply's patch: alternatives to it, so the patch is
 * only offered. Chips after a line that follows the patch answer that line
 * (O's next question, DEV-3569): the patch is made, and they stay with it.
 */
const offersChipsWith = (blocks: readonly CopilotBlock[]) => {
  const patch = blocks.findIndex((block) => block.type === "patch");
  return blocks.some(
    (block, index) =>
      block.type === "chips" &&
      !(index > patch && blocks.slice(patch + 1, index).some((one) => one.type === "text")),
  );
};

export const changeKey = (change: CopilotRuleChange) => change.paths.join(" ");

const toOpsOf = (change: CopilotRuleChange) =>
  change.paths.map((path, index) => ({
    path,
    to: change.to[index],
    toPresent: change.toPresent?.[index],
    toAbsentParents: change.toAbsentParents?.[index],
  }));

const fromOpsOf = (change: CopilotRuleChange) =>
  change.paths.map((path, index) => ({
    path,
    to: change.from[index] ?? null,
    toPresent: change.fromPresent?.[index] ?? change.from[index] != null,
    toAbsentParents: change.fromAbsentParents?.[index],
  }));

export const fixLabelOf = (finding: CopilotFinding, fix: string) =>
  finding.fix?.title.trim() || fix;

export const isDoing = (chip: CopilotChip) => Boolean(chip.pick || chip.say?.trim());

// The backend pins this id on a failed run's card fix: it changes no rule, the page generates.
const GENERATE_AGAIN = "generate_again";

export const isGenerateAgain = (patch: Pick<CopilotPatch, "id" | "ops">) =>
  patch.id === GENERATE_AGAIN && patch.ops.length === 0;

export const isRunAsIs = ({ pick, say }: CopilotChip) =>
  !pick && /^\s*run (it )?as is\s*[.!]*\s*$/i.test(say ?? "");

// The backend pins these words on its CDT Studio chip.
const OPEN_CDT_STUDIO = "open CDT Studio";

export const isOpenCdtStudio = ({ pick, say }: CopilotChip) =>
  !pick && say?.trim() === OPEN_CDT_STUDIO;

const OPEN_STORE = "open_store_";

export interface OpenedStore {
  store: string;
  storePogId?: number;
}

export const chipKey = ({ pick, say }: CopilotChip) => {
  if (!pick) return `say:${say?.trim() ?? ""}`;
  if (pick.kind === "move") return `move:${pick.id}`;
  if (pick.kind === "option") return `option:${pick.choice}:${pick.option}`;
  return `fix:${pick.finding}`;
};

const BLOCKING = "merchandising.vertical_blocking_mode";
const ENGINE_BLOCKING = "high_density_mode.vertical_blocking_mode";

const TALL_CHOICE = "tall";
const BANDS = "merchandising.item_shelf_bands";

const hasTallBand = (rules: PspRules) => {
  const bands = valueAt(rules, BANDS);
  return (
    typeof bands === "object" &&
    bands !== null &&
    Object.values(bands as Record<string, unknown>).some(
      (band) => typeof band === "object" && band !== null,
    )
  );
};

const NAMED_IN_LINE = 3;

const nameOfPath = (path: string) => {
  const keys = path.split(".");
  const words = (keys[1] ?? keys[0])
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const namesOf = (paths: readonly string[], rows: readonly CopilotSettingRow[]) => {
  const shown = paths.includes(BLOCKING) ? paths.filter((path) => path !== ENGINE_BLOCKING) : paths;
  const named = shown.map((path, index) => {
    const row = rows.findIndex(
      (candidate) => path === candidate.path || path.startsWith(`${candidate.path}.`),
    );
    return row < 0
      ? { name: nameOfPath(path), order: rows.length + index }
      : { name: rows[row].label, order: row };
  });
  named.sort((a, b) => a.order - b.order);
  return [...new Set(named.map(({ name }) => name))];
};

export const cardKey = (entryId: number, index: number | "applied") => `${entryId}:${index}`;

export type CardState = "on" | "again" | "offered";

type TurnState = Omit<ResponsePostConfigCopilotTurn, "message">;

interface PendingTap {
  token: number;

  from: string;
  patch: CopilotPatch;

  after: PspRules;
}

export const useConfigCopilot = ({
  merchflowId,
  basePogId,
  blocking,
  readRules,
  writeRules,
  pendingFix,
  onGenerate,
}: Options) => {
  const { t } = useTranslation("common");
  const [entries, setEntries] = useState<CopilotEntry[]>([]);
  const [asking, setAsking] = useState(0);
  const [thinking, setThinking] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [briefFailed, setBriefFailed] = useState(false);
  const [trouble, setTrouble] = useState<CopilotTrouble | null>(null);
  const [headline, setHeadline] = useState("");
  const [choices, setChoices] = useState<CopilotChoice[]>([]);
  const [flips, setFlips] = useState<Record<string, string>>({});
  const [alwaysOn, setAlwaysOn] = useState<string[]>([]);
  const [findings, setFindings] = useState<CopilotFinding[]>([]);
  const [idle, setIdle] = useState<CopilotIdleSwitch[]>([]);
  const [suggestions, setSuggestions] = useState<CopilotSuggestion[]>([]);

  const [recommendation, setRecommendation] = useState<CopilotChip | null>(null);

  const [observations, setObservations] = useState<CopilotObservation[]>([]);
  const [concierge, setConcierge] = useState<ResponsePostConfigCopilotBrief["concierge"]>();
  const [conciergeReady, setConciergeReady] = useState<boolean | undefined>();
  const [setupRows, setSetupRows] = useState<CopilotSetupRow[]>([]);
  const [setupApplied, setSetupApplied] = useState<CopilotRuleChange[]>([]);
  const [kept, setKept] = useState<CopilotKept[]>([]);
  const [asks, setAsks] = useState<CopilotChip[]>([]);
  const [runState, setRunState] = useState<CopilotRunState | undefined>(undefined);
  // True while the model rewords the opening; the brief's words show until then.
  const [isWording, setIsWording] = useState(false);
  const [question, setQuestion] = useState("");
  const [openingEntry, setOpeningEntry] = useState<number | null>(null);
  const wording = useRef<{ said: number; rules: PspRules } | null>(null);

  const [usedChips, setUsedChips] = useState<string[]>([]);

  const [isAdjustAsked, setIsAdjustAsked] = useState(false);
  const [changes, setChanges] = useState<CopilotRuleChange[]>([]);
  const [lastRun, setLastRun] = useState<CopilotLastRun | null>(null);
  const [hasLastRunRules, setHasLastRunRules] = useState(false);
  const [copyCompatibility, setCopyCompatibility] =
    useState<CopilotFactSheet["copyCompatibility"]>();
  const [reviewedSettings, setReviewedSettings] = useState<CopilotReviewedSetting[]>([]);
  const reviewed = useRef<CopilotReviewedSetting[]>([]);
  const [runDetails, setRunDetails] = useState<{
    readout: CopilotReadout | null;
    findings: CopilotFinding[];
  } | null>(null);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);

  const [made, setMade] = useState<Record<string, boolean>>({});

  const [byYou, setByYou] = useState<string[]>([]);
  const factSheet = useRef<CopilotFactSheet | undefined>(undefined);
  const lastRunRules = useRef<PspRules | null>(null);

  const oSetup = useRef<PspRules | null>(null);

  const beforeAdvanced = useRef<PspRules | null>(null);
  const isEditedInAdvanced = useRef(false);

  const settingRows = useRef<CopilotSettingRow[]>([]);
  const nextId = useRef(1);

  const strip = useRef<CopilotRuleChange[]>([]);
  const awaitingRows = useRef<MadeChange[]>([]);
  // Serialize turns so each request reads the rules left by the previous one.
  const turns = useRef<Promise<unknown>>(Promise.resolve());

  const sentTurns = useRef(0);
  // A response may only replace state from an earlier turn and unchanged rules.
  const shownTurn = useRef(0);
  // A reply from another base planogram must not update this panel.
  const generation = useRef(0);
  const journal = useRef<CopilotPatch[]>([]);
  const redoJournal = useRef<CopilotPatch[]>([]);
  const journalActions = useRef(
    new WeakMap<CopilotPatch, { patch: CopilotPatch; undoing: boolean }>(),
  );
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  const taps = useRef<Record<string, PendingTap>>({});
  const nextTap = useRef(1);
  const pending = useRef(pendingFix);
  pending.current = pendingFix;
  const isFixTaken = useRef(false);
  const isFixLoading = useRef(false);
  const pendingRunFinding = useRef<CopilotFinding | null>(null);
  const generateRef = useRef(onGenerate);
  generateRef.current = onGenerate;

  // The parent exposes a write on the next render; use the written rules until then.

  const reader = useRef(readRules);
  reader.current = readRules;
  const written = useRef<PspRules | null>(null);
  useEffect(() => {
    written.current = null;
  });
  const current = () => written.current ?? reader.current();
  const put = (rules: PspRules) => {
    const moved = changedPaths(current(), rules);
    setByYou((paths) =>
      paths.some((path) => moved.includes(path))
        ? paths.filter((path) => !moved.includes(path))
        : paths,
    );
    written.current = rules;
    writeRules(rules);
  };

  const add = (message: CopilotMessage, applied?: CopilotEntry["applied"]) => {
    const id = nextId.current++;
    setEntries((all) => [...all, { ...message, id, applied }]);
    return id;
  };

  const recordedPatch = (patch: CopilotPatch, before: PspRules, after: PspRules): CopilotPatch => ({
    ...patch,
    ops: patch.ops.map((op) => ({
      ...op,
      from: valueAt(before, op.path) ?? null,
      fromPresent: presentAt(before, op.path),
      fromAbsentParents: absentParents(before, op.path),
      to: valueAt(after, op.path) ?? null,
      toPresent: presentAt(after, op.path),
      toAbsentParents: absentParents(after, op.path),
    })),
  });
  const rememberPatch = (patch: CopilotPatch, before: PspRules, after: PspRules) => {
    if (changedPaths(before, after).length === 0) return;
    journal.current.push(recordedPatch(patch, before, after));
    redoJournal.current = [];
  };

  // Each row O wrote is its own change, so the row's Undo and a typed "undo" reverse it alone.
  const journalSetup = (
    applied: readonly CopilotRuleChange[],
    before: PspRules,
    after: PspRules,
  ) => {
    const moved = new Set(changedPaths(before, after));
    for (const row of applied) {
      const ops = row.paths
        .filter((path) => [...moved].some((one) => overlaps(one, path)))
        .map((path) => ({ path, label: row.label, from: null, to: null }));
      if (ops.length === 0) continue;
      journal.current.push(recordedPatch(pagePatch(SETUP_PATCH, row.label, ops), before, after));
    }
    redoJournal.current = [];
  };

  const moveJournal = (
    patch: CopilotPatch,
    undoing: boolean,
    alreadyTaken = false,
    aggregate = false,
  ) => {
    const source = undoing ? journal.current : redoJournal.current;
    const paths = new Set(patch.ops.map((op) => op.path));
    const sameTransition = (entry: CopilotPatch) =>
      entry.id === patch.id &&
      entry.ops.some((op) => paths.has(op.path)) &&
      entry.ops
        .filter((op) => paths.has(op.path))
        .every((op) =>
          patch.ops.some(
            (asked) =>
              op.path === asked.path &&
              isSameValue(op.from, asked.from) &&
              isSameValue(op.to, asked.to) &&
              (asked.fromPresent === undefined || op.fromPresent === asked.fromPresent) &&
              (asked.toPresent === undefined || op.toPresent === asked.toPresent) &&
              (asked.fromAbsentParents === undefined ||
                isSameValue(op.fromAbsentParents, asked.fromAbsentParents)) &&
              (asked.toAbsentParents === undefined ||
                isSameValue(op.toAbsentParents, asked.toAbsentParents)),
          ),
        );
    const exact = source.lastIndexOf(patch);
    const index = alreadyTaken ? -1 : exact >= 0 ? exact : source.findLastIndex(sameTransition);
    const recorded = index >= 0 && !aggregate ? source[index] : patch;
    const applicable = recorded.ops.filter(
      (op) => paths.has(op.path) && holds(current(), undoing ? [op] : undoOps([op])),
    );
    if (applicable.length === 0) return undefined;
    const before = current();
    const ops = undoing ? undoOps(applicable) : applicable;
    const after = patchedRules(before, ops);
    put(after);
    if (!alreadyTaken) {
      // A card's Undo takes its whole entry, fields edited back by hand included.
      const reversed = aggregate ? new Set(applicable.map((op) => op.path)) : paths;
      const kept = source.flatMap((entry, position) => {
        if (!aggregate && position !== index) return [entry];
        const remaining = entry.ops.filter((op) => !reversed.has(op.path));
        if (remaining.length === 0) return [];
        // Keep canonical identity for an earlier displayed reversal.
        entry.ops = remaining;
        return [entry];
      });
      if (undoing) journal.current = kept;
      else redoJournal.current = kept;
    }
    const movedPatch = recordedPatch(
      { ...patch, ops: applicable },
      undoing ? after : before,
      undoing ? before : after,
    );
    (undoing ? redoJournal.current : journal.current).push(movedPatch);
    return { before, after, ops, patch: movedPatch };
  };
  const awaitRows = (id: number, patch: CopilotPatch, before: PspRules, after: PspRules) => {
    rememberPatch(patch, before, after);
    awaitingRows.current.push({
      id,
      paths: patch.ops.map(({ path }) => path),
      before,
      after,
      strip: strip.current,
    });
  };

  // A reply's one patch is made at once: journalled, its card's Undo live, the strip told.
  const applyReply = (blocks: readonly CopilotBlock[], single: PatchBlock) => {
    const before = current();
    const rules = patchedRules(before, single.patch.ops);
    put(rules);
    const rest = blocks.filter((block) => block.type !== "patch");
    const hasLine = rest.some((block) => block.type === "text" && block.text.trim() !== "");
    const line: CopilotBlock = {
      type: "text",
      text: t("configCopilot.doneTitle", { title: single.patch.title }),
    };
    const id = add(
      { role: "copilot", blocks: hasLine ? rest : [line, ...rest] },
      { patch: single.patch, raises: single.raises ?? [] },
    );
    setMade((all) => ({ ...all, [cardKey(id, "applied")]: true }));
    awaitRows(id, single.patch, before, rules);
    outdateSent();
    return id;
  };

  const context = (rules: PspRules) => ({ base_pog_id: basePogId, psp_rules: rules, blocking });

  const turn = (
    payload: () => Parameters<typeof postConfigCopilotTurn>[1],
    isWanted: () => boolean = () => true,
  ) => {
    if (concierge) setConciergeReady(false);
    const started = generation.current;
    const sent = turns.current.then(async () => {
      if (generation.current !== started || !isWanted()) throw new TurnNotSent();
      const order = ++sentTurns.current;
      const sentPayload = {
        ...payload(),
        reviewed_settings: reviewed.current.filter(({ path, value }) =>
          isSameValue(valueAt(current(), path), value),
        ),
      };
      const snapshot = structuredClone(current());
      const response = await postConfigCopilotTurn(merchflowId, sentPayload);
      // Paths changed while this turn was out: a pending tap's may stay, any other edit drops the answer.
      const tapped = changedPaths(snapshot, current());
      if (
        generation.current !== started ||
        tapped.some(
          (path) =>
            !Object.values(taps.current).some((tap) =>
              tap.patch.ops.some((op) => overlaps(op.path, path) && holds(current(), [op])),
            ),
        )
      )
        throw new TurnNotSent();
      return { response, order, tapped };
    });
    turns.current = sent.catch(() => undefined);
    return sent;
  };

  const outdateSent = () => {
    shownTurn.current = sentTurns.current;
  };

  const sheet = () =>
    factSheet.current && {
      ...factSheet.current,
      lastRunRules: lastRunRules.current,
      setupRules: oSetup.current,
    };

  const idleOf = (state: TurnState): CopilotIdleSwitch[] => {
    if (state.choices.some(({ id }) => id === BRAND_BLOCKS.id)) return [];
    const row = state.settings.find(({ path }) => path === BRAND_BLOCKS.path);
    const blocking = state.choices.find(({ id }) => id === BLOCKING_CHOICE);
    const isHorizontal = blocking?.options.some(
      ({ selected, label }) => selected && /horizontal/i.test(label),
    );
    if (row?.status !== "not_applicable" && !isHorizontal) return [];
    return [
      {
        id: BRAND_BLOCKS.id,
        label: t("configCopilot.brandBlocks"),
        reason:
          (row?.status === "not_applicable" && row.statusReason) ||
          t("configCopilot.notUnderHorizontal"),
        after: BLOCKING_CHOICE,
      },
    ];
  };

  // Any reply may carry these, an auto-applied one included, so the next turn sends them.
  const takeReviewed = (settings: CopilotReviewedSetting[] | undefined) => {
    if (!settings) return;
    reviewed.current = settings;
    setReviewedSettings(settings);
  };
  const takeFacts = (blocks: readonly CopilotBlock[]) => {
    const fresh = blocks.findLast((block) => block.type === "facts");
    if (fresh?.type !== "facts") return;
    factSheet.current = fresh.factSheet;
    setCopyCompatibility(fresh.factSheet.copyCompatibility);
  };

  const take = (state: TurnState) => {
    if (state.concierge) setConcierge(state.concierge);
    if (state.conciergeReady !== undefined) setConciergeReady(state.conciergeReady);
    takeReviewed(state.reviewedSettings);
    settingRows.current = state.settings;
    setChoices(state.choices);
    setIdle(idleOf(state));
    setAlwaysOn(state.alwaysOn);
    setFindings(state.findings);
    setSuggestions(state.suggestions);
    setRecommendation(state.suggestions.find(isDoing) ?? null);
    setChanges(state.changesSinceLastRun);
    strip.current = state.changesSinceLastRun;
    const waiting = awaitingRows.current;
    if (waiting.length === 0) return;
    awaitingRows.current = [];
    const rows = new Map(waiting.map((made) => [made.id, rowsOf(made, state.changesSinceLastRun)]));
    setEntries((all) =>
      all.map((entry) => {
        const changes = rows.get(entry.id);
        return changes && entry.applied
          ? { ...entry, applied: { ...entry.applied, changes } }
          : entry;
      }),
    );
  };

  const takeTurn = ({
    response,
    order,
  }: {
    response: ResponsePostConfigCopilotTurn;
    order: number;
  }) => {
    if (order <= shownTurn.current) return;
    shownTurn.current = order;
    takeFacts(response.message.blocks);
    take(response);
  };

  const brief = async () => {
    const started = generation.current;
    setBriefFailed(false);
    if (concierge) {
      setIsReady(false);
      setConciergeReady(false);
    }
    const payload = { ...context(current()), start_from_last_run: true };
    try {
      const response = await withinTime(
        postConfigCopilotBrief(merchflowId, payload),
        BRIEF_TIMEOUT_MS,
      );
      if (generation.current !== started) return;
      const served = SERVED_FROM.has(response.rulesFrom) ? response.rules : undefined;
      // Edits the user made in Advanced while O read stay; O's rules become its picks to go back to.
      const isEdited =
        isEditedInAdvanced.current ||
        (beforeAdvanced.current !== null &&
          changedPaths(beforeAdvanced.current, current()).length > 0);
      if (served && !isEdited) {
        const before = current();
        put(served);
        if (response.rulesFrom === "o_setup")
          journalSetup(response.setupApplied ?? [], before, served);
      }
      const rules = served ?? current();
      factSheet.current = response.factSheet;
      setCopyCompatibility(response.factSheet.copyCompatibility);
      lastRunRules.current = response.lastRunRules;
      oSetup.current = rules;
      setHasLastRunRules(response.lastRunRules !== null);
      setHeadline(response.headline);
      setLastRun(response.lastRun);
      setObservations(response.observations ?? []);
      setConcierge(response.concierge);
      setConciergeReady(response.conciergeReady);
      setSetupRows(response.setup ?? []);
      setSetupApplied(response.setupApplied ?? []);
      setKept(response.kept ?? []);
      setAsks((response.asks ?? []).filter(isDoing).slice(0, MAX_ASKS));
      // The base's question shows above its chips from the brief: the model only rewords it.
      setQuestion(response.question?.trim() ?? "");
      setRunState(response.runState);
      take(response);
      setIsReady(true);
      if (served && isEdited) refreshAfter();
      takePendingFix(response.findings, response.lastRun);
      if (!isEdited) void reword(payload);
    } catch {
      if (generation.current === started) setBriefFailed(true);
    }
  };

  // Stale once the user typed, tapped or changed a rule since the opening was asked for.
  const isUntouched = ({ said, rules }: { said: number; rules: PspRules }) =>
    nextId.current === said && changedPaths(rules, current()).length === 0;

  // The model's words over the brief, by its ids; anything missing, late or failed keeps the brief's.
  const reword = async (payload: Parameters<typeof postConfigCopilotOpening>[1]) => {
    const started = generation.current;
    const asked = { said: nextId.current, rules: structuredClone(current()) };
    wording.current = asked;
    const isCurrent = () => generation.current === started && isUntouched(asked);
    setIsWording(true);
    try {
      const words = await withinTime(
        postConfigCopilotOpening(merchflowId, payload),
        OPENING_TIMEOUT_MS,
      );
      if (!isCurrent() || words.reader === "offline") return;
      if (words.headline?.trim()) setHeadline(words.headline);
      const lines = new Map(words.observations?.map(({ id, line }) => [id, line.trim()]));
      setObservations((all) => all.map((one) => ({ ...one, line: lines.get(one.id) || one.line })));
      const reasons = new Map(words.setup?.map(({ label, reason }) => [label, reason.trim()]));
      setSetupRows((all) =>
        all.map((row) => ({ ...row, reason: reasons.get(row.label) || row.reason })),
      );
      if (words.question?.trim()) setQuestion(words.question.trim());
      const single = singlePatchOf(words.blocks ?? []);
      if (single && !holds(current(), single.patch.ops)) {
        setOpeningEntry(applyReply(words.blocks ?? [], single));
        refreshAfter();
      }
    } catch {
      // The brief already said it; the model's words are only a better way to say it.
    } finally {
      if (generation.current === started) setIsWording(false);
    }
  };

  useEffect(() => {
    generation.current++;
    journal.current = [];
    redoJournal.current = [];
    journalActions.current = new WeakMap();
    nextId.current = 1;
    strip.current = [];
    awaitingRows.current = [];
    factSheet.current = undefined;
    reviewed.current = [];
    setReviewedSettings([]);
    setCopyCompatibility(undefined);
    lastRunRules.current = null;
    oSetup.current = null;
    beforeAdvanced.current = null;
    isEditedInAdvanced.current = false;
    taps.current = {};
    isFixLoading.current = false;
    pendingRunFinding.current = null;
    setEntries([]);
    setAsking(0);
    setThinking(0);
    setMade({});
    setByYou([]);
    setFlips({});
    setTrouble(null);
    setRunDetails(null);
    setRecommendation(null);
    setObservations([]);
    setConcierge(undefined);
    setConciergeReady(undefined);
    setSetupRows([]);
    setSetupApplied([]);
    setKept([]);
    setAsks([]);
    setRunState(undefined);
    setIsWording(false);
    wording.current = null;
    setQuestion("");
    setOpeningEntry(null);
    setUsedChips([]);
    setIsAdjustAsked(false);
    setIsReady(false);
    void brief();
  }, [merchflowId, basePogId]);

  const takePendingFix = (now: readonly CopilotFinding[], run: CopilotLastRun | null) => {
    const asked = pending.current;
    if (!asked || isFixTaken.current || isFixLoading.current) return;
    if (pendingRunFinding.current) {
      if (!generateRef.current) return;
      isFixTaken.current = true;
      makeFix(pendingRunFinding.current);
      pendingRunFinding.current = null;
      asked.onTaken();
      return;
    }
    // The modal can finish O's brief before its parent enables Generate. Keep this link
    // until the page can actually run the action, instead of consuming it as an empty fix.
    const currentFinding = now.find((one) => one.id === asked.finding && one.fix);
    if (
      asked.finding !== undefined &&
      (asked.run === undefined || asked.run === run?.storePogTriggerId) &&
      currentFinding?.fix &&
      isGenerateAgain(currentFinding.fix) &&
      !generateRef.current
    )
      return;
    isFixTaken.current = true;
    if (asked.finding === undefined && asked.adjust) {
      setIsAdjustAsked(true);
      asked.onTaken();
      return;
    }
    if (asked.finding === undefined) {
      readRunDetails(asked.run ?? run?.storePogTriggerId);
      asked.onTaken();
      return;
    }
    if (asked.run !== undefined && asked.run !== run?.storePogTriggerId) {
      isFixTaken.current = false;
      isFixLoading.current = true;
      fixOnRun(asked.finding, asked.run, now, asked.onTaken);
      return;
    }
    fixFound(
      now.find((one) => one.id === asked.finding && one.fix),
      "configCopilot.alreadyFixed",
    );
    asked.onTaken();
  };

  useEffect(() => {
    if (isReady && onGenerate && !isFixTaken.current) takePendingFix(findings, lastRun);
  }, [isReady, onGenerate, findings, lastRun]);

  const fixFound = (
    finding: CopilotFinding | undefined,
    missingMessage: "configCopilot.alreadyFixed" | "configCopilot.fixUnavailable",
  ) => {
    if (finding) makeFix(finding);
    else add({ role: "copilot", blocks: [{ type: "text", text: t(missingMessage) }] });
  };

  const fixOnRun = (
    id: string,
    run: number,
    now: readonly CopilotFinding[],
    onTaken: () => void,
  ) => {
    isFixLoading.current = true;
    const started = generation.current;
    postConfigCopilotReadout(merchflowId, {
      base_pog_id: basePogId,
      store_pog_trigger_id: run,
      psp_rules: current(),
      blocking,
    })
      .then((response) => {
        if (generation.current !== started) return;
        isFixLoading.current = false;
        const found =
          response.findings.find((one) => one.id === id && one.fix) ??
          now.find((one) => one.id === id && one.fix && one.source === "rules");
        if (found?.fix && isGenerateAgain(found.fix) && !generateRef.current) {
          pendingRunFinding.current = found;
          return;
        }
        isFixTaken.current = true;
        fixFound(found, "configCopilot.fixUnavailable");
        onTaken();
      })
      .catch(() => {
        if (generation.current !== started) return;
        isFixLoading.current = false;
        setTrouble({
          line: t("configCopilot.notDone"),
          retry: () => fixOnRun(id, run, now, onTaken),
        });
      });
  };

  const refresh = async () => {
    const started = generation.current;
    // The server reads answered decisions from the history, so a reread carries it too.
    const answer = await turn(() => ({
      ...context(current()),
      history: historyOf(entriesRef.current),
      fact_sheet: sheet(),
    }));
    if (generation.current === started) takeTurn(answer);
  };

  const refreshAfter = () => {
    const started = generation.current;
    refresh().catch(() => {
      if (generation.current !== started) return;
      setTrouble({ line: t("configCopilot.notReread"), retry: refreshAfter });
    });
  };

  const writeChange = (ops: Parameters<typeof patchedRules>[1], patch?: CopilotPatch) => {
    const before = current();
    const rules = patchedRules(before, ops);
    rememberPatch(
      {
        ...pagePatch(
          "local_change",
          "Change rules",
          ops.map((op) => ({ ...op, label: op.path, from: valueAt(before, op.path) ?? null })),
        ),
        ...patch,
      },
      before,
      rules,
    );
    put(rules);
    outdateSent();
    setTrouble(null);
    refreshAfter();
  };

  // The pick response already includes its patch, so applying it again would duplicate the fix.
  const makeFix = (finding: CopilotFinding) => {
    const patch = finding.fix;
    if (!patch) return;
    if (isGenerateAgain(patch) && generateRef.current) {
      generateRef.current();
      return;
    }
    const started = generation.current;
    add({
      role: "user",

      blocks: [{ type: "text", text: fixLabelOf(finding, t("configCopilot.fix")) }],
    });
    if (holds(current(), patch.ops)) {
      add({ role: "copilot", blocks: [{ type: "text", text: t("configCopilot.alreadyFixed") }] });
      return;
    }
    const line = t("configCopilot.doneTitle", { title: patch.title });
    const id = add(
      { role: "copilot", blocks: [{ type: "text", text: line }] },
      { patch, raises: [] },
    );
    setMade((all) => ({ ...all, [cardKey(id, "applied")]: true }));
    const before = current();
    const after = patchedRules(before, patch.ops);
    awaitRows(id, patch, before, after);
    put(after);
    outdateSent();
    setTrouble(null);

    void (async () => {
      try {
        const answer = await turn(() => ({
          ...context(
            patchedRules(
              current(),
              patch.ops.map(({ path }) => ({ path, to: valueAt(before, path) })),
            ),
          ),
          pick: { kind: "fix", finding: finding.id, patch },
          fact_sheet: sheet(),
        }));
        if (generation.current !== started) return;

        if (singlePatchOf(answer.response.message.blocks)) takeTurn(answer);
        else {
          const back = patch.ops
            .filter((op) => holds(current(), [op]))
            .map((op) => ({
              path: op.path,
              to: valueAt(before, op.path) ?? null,
              toPresent: presentAt(before, op.path),
              toAbsentParents: absentParents(before, op.path),
            }));
          if (back.length) put(patchedRules(current(), back));
          setEntries((all) =>
            all.map((entry) =>
              entry.id === id
                ? { ...entry, ...answer.response.message, applied: undefined }
                : entry,
            ),
          );
          takeTurn(answer);
        }
      } catch {
        if (generation.current !== started) return;
        setTrouble({ line: t("configCopilot.notReread"), retry: refreshAfter });
      }
    })();
  };

  const ask = async (input: CopilotAsk): Promise<boolean> => {
    const started = generation.current;
    const isCurrent = () => generation.current === started;
    const isText = "text" in input;
    if (
      isText &&
      /^\s*(undo|redo)( that| it| this| the last( one| change)?)?[.!\s]*$/i.test(input.text)
    ) {
      const undoing = /^\s*undo/i.test(input.text);
      const source = undoing ? journal.current : redoJournal.current;
      // Only ever the latest change; one edited since is the server's to explain.
      const patch = source.at(-1);
      const moved = patch && moveJournal(patch, undoing, true);
      if (patch && moved) {
        source.pop();
        const { before, after, ops } = moved;
        const inverse: CopilotPatch = {
          ...patch,
          id: `${undoing ? "undo" : "redo"}_${patch.id}`,
          title: t(undoing ? "configCopilot.redoTitle" : "configCopilot.undoTitle", {
            title: patch.title,
          }),
          ops: ops.map((op) => ({
            ...op,
            label: op.path,
            from: valueAt(before, op.path) ?? null,
            fromPresent: presentAt(before, op.path),
            fromAbsentParents: absentParents(before, op.path),
          })),
        };
        journalActions.current.set(inverse, { patch: moved.patch, undoing: !undoing });
        add(
          {
            role: "copilot",
            blocks: [
              { type: "text", text: t(undoing ? "configCopilot.undone" : "configCopilot.redone") },
            ],
          },
          {
            patch: inverse,
            raises: [],
            changes: rowsOf(
              { id: -1, paths: ops.map((op) => op.path), before, after, strip: strip.current },
              strip.current,
            ),
          },
        );
        outdateSent();
        refreshAfter();
        return true;
      }
    }
    const said = add({
      role: "user",
      blocks: [{ type: "text", text: isText ? input.text : input.shown }],
    });
    setTrouble(null);
    setAsking((count) => count + 1);
    let isThinking = false;
    const timer = setTimeout(() => {
      isThinking = true;
      setThinking((count) => count + 1);
    }, THINKING_AFTER_MS);

    try {
      const answer = await turn(() => ({
        ...context(current()),
        ...(isText ? { text: input.text } : { pick: input.pick }),
        history: historyOf(
          entriesRef.current.filter((entry) => entry.role === "copilot" || entry.id < said),
        ),
        fact_sheet: sheet(),
      }));
      if (!isCurrent()) return false;
      const { response } = answer;

      // A fresh answer from O takes ownership when it explicitly confirms the
      // merchant's current Advanced value. A background reread never does.
      if (response.reviewedSettings?.length) {
        setByYou((paths) =>
          paths.filter(
            (path) =>
              !response.reviewedSettings?.some(
                (review) =>
                  review.path === path && isSameValue(valueAt(current(), path), review.value),
              ),
          ),
        );
      }

      const found = singlePatchOf(response.message.blocks);
      const ops = found?.patch.ops.filter(
        ({ path }) => !answer.tapped.some((held) => overlaps(held, path)),
      );
      const single = found && ops?.length ? { ...found, patch: { ...found.patch, ops } } : null;

      if (!single || offersChipsWith(response.message.blocks)) {
        add(response.message);
        takeTurn(answer);
        return true;
      }

      applyReply(response.message.blocks, single);
      takeFacts(response.message.blocks);
      if (isText) {
        takeReviewed(response.reviewedSettings);
        refreshAfter();
      } else take(response);
      return true;
    } catch (error) {
      if (!isCurrent()) return false;

      if (isText && error instanceof OUnavailableError) {
        add({ role: "copilot", blocks: [{ type: "text", text: t("configCopilot.offline") }] });
        return true;
      }
      setEntries((all) => all.filter((entry) => entry.id !== said));
      setTrouble({ line: t("configCopilot.notDone"), retry: () => void ask(input) });
      return false;
    } finally {
      clearTimeout(timer);
      if (isCurrent()) {
        setAsking((count) => count - 1);
        if (isThinking) setThinking((count) => count - 1);
      }
    }
  };

  const pickOption = (choice: CopilotChoice, option: CopilotOption) => {
    const tapped = taps.current[choice.id];
    if (tapped && option.label === tapped.from) {
      dropTap(choice.id);
      writeChange(undoOps(tapped.patch.ops.filter((op) => holds(current(), [op]))));
      return;
    }
    const patch = option.patch;
    if (!patch || option.selected) return;
    const started = generation.current;
    const token = nextTap.current++;
    const isLatest = () => taps.current[choice.id]?.token === token;

    const isWanted = () => generation.current === started && isLatest();
    const from = tapped?.from ?? choice.options.find(({ selected }) => selected)?.label ?? "";
    setFlips((all) => ({ ...all, [choice.id]: option.label }));
    setTrouble(null);
    const before = current();
    const after = patchedRules(before, patch.ops);
    taps.current = { ...taps.current, [choice.id]: { token, from, patch, after } };
    put(after);
    outdateSent();

    void (async () => {
      try {
        // The server evaluates the pick against the rules before its own optimistic change.
        const answer = await turn(
          () => ({
            ...context(
              patchedRules(
                current(),
                // A null previous value means the key was absent, not explicitly null.
                patch.ops.map(({ path, from }) => ({ path, to: from ?? undefined })),
              ),
            ),
            pick: { kind: "option", choice: choice.id, option: patch.id },
            fact_sheet: sheet(),
          }),
          isWanted,
        );

        if (!isWanted()) return;

        const served = singlePatchOf(answer.response.message.blocks)?.patch;
        const rules = current();
        const ops = (served?.ops ?? []).filter(
          (op) =>
            isSameValue(valueAt(rules, op.path), valueAt(after, op.path)) && !holds(rules, [op]),
        );
        if (ops.length > 0) put(patchedRules(rules, ops));

        if (served) {
          rememberPatch(served, before, current());
          takeTurn(answer);
        } else await refresh();
      } catch {
        if (!isWanted()) return;
        const rules = current();
        const back = patch.ops.filter((op) => holds(rules, [op]));
        if (back.length > 0) put(patchedRules(rules, undoOps(back)));
        setTrouble({ line: t("configCopilot.notSwitched", { label: choice.label }) });
      } finally {
        if (isWanted()) dropTap(choice.id);
      }
    })();
  };

  const dropTap = (id: string) => {
    taps.current = Object.fromEntries(Object.entries(taps.current).filter(([key]) => key !== id));
    setFlips((all) => Object.fromEntries(Object.entries(all).filter(([key]) => key !== id)));
  };

  const advancedLine = (paths: readonly string[]) => {
    const names = namesOf(paths, settingRows.current);
    const rest = names.length - NAMED_IN_LINE;
    const listed = names.slice(0, NAMED_IN_LINE).join(", ");
    return t(
      names.length === 1 ? "configCopilot.advancedChangedOne" : "configCopilot.advancedChangedMany",
      {
        count: names.length,
        names: rest > 0 ? t("configCopilot.andMore", { names: listed, count: rest }) : listed,
      },
    );
  };

  const readRunDetails = (run: number | undefined) => {
    const started = generation.current;
    setIsDetailsLoading(true);
    postConfigCopilotReadout(merchflowId, {
      base_pog_id: basePogId,
      store_pog_trigger_id: run,
      blocking,
    })
      .then((response) => {
        if (generation.current === started) {
          setRunDetails({ readout: response.readout, findings: response.findings });
        }
      })
      .catch(() => {
        if (generation.current === started) {
          setTrouble({ line: t("configCopilot.noDetails"), retry: () => readRunDetails(run) });
        }
      })
      .finally(() => {
        if (generation.current === started) setIsDetailsLoading(false);
      });
  };

  const toggleRunDetails = () => {
    if (runDetails) setRunDetails(null);
    // The run the headline named.
    else readRunDetails(lastRun?.storePogTriggerId);
  };

  let onScreen: PspRules | undefined;

  const undoChange = (row: CopilotRuleChange) => {
    setChanges((all) => all.filter((other) => changeKey(other) !== changeKey(row)));
    const patch = pagePatch(
      "row_change",
      row.label,
      row.paths.map((path, index) => ({
        path,
        label: row.label,
        from: row.from[index] ?? null,
        fromPresent: row.fromPresent?.[index] ?? row.from[index] != null,
        fromAbsentParents: row.fromAbsentParents?.[index],
        to: row.to[index],
        toPresent: row.toPresent?.[index],
        toAbsentParents: row.toAbsentParents?.[index],
      })),
    );
    if (moveJournal(patch, true, false, true)) {
      outdateSent();
    }
    setTrouble(null);
    refreshAfter();
  };

  const setupStateOf = ({ change }: CopilotSetupRow): SetupRowState | undefined => {
    if (!change) return undefined;
    onScreen ??= current();
    if (holds(onScreen, toOpsOf(change))) return "on";
    return holds(onScreen, fromOpsOf(change)) ? "off" : "moved";
  };

  const undoSetup = ({ change }: CopilotSetupRow) => {
    if (!change) return;
    const written = journal.current.findLast(
      (entry) =>
        entry.id === SETUP_PATCH && entry.ops.every(({ path }) => change.paths.includes(path)),
    );
    if (!written) {
      undoChange(change);
      return;
    }
    if (moveJournal(written, true)) outdateSent();
    setTrouble(null);
    refreshAfter();
  };

  const setupAgain = ({ change }: CopilotSetupRow) => {
    if (!change) return;
    const ops = toOpsOf(change);
    writeChange(
      ops,
      pagePatch(
        SETUP_PATCH,
        change.label,
        ops.map((op) => ({ ...op, label: change.label, from: null })),
      ),
    );
  };

  const palette = useMemo(
    () =>
      sketchPaletteOf([
        ...observations.flatMap(({ sketch }) => (sketch ? sketch.options : [])),
        ...entries.flatMap(({ blocks }) =>
          blocks.flatMap((block) =>
            block.type === "observation" && block.observation.sketch
              ? block.observation.sketch.options
              : [],
          ),
        ),
      ]),
    [observations, entries],
  );

  const rulesOnScreen = current();
  const marksNow = useMemo(() => {
    const lines: Record<string, string> = {};
    for (const { path, value, kind, reason } of reviewedSettings) {
      if (byYou.includes(path) || !isSameValue(valueAt(rulesOnScreen, path), value)) continue;
      lines[path] = t(kind === "set" ? "configCopilot.setByO" : "configCopilot.confirmedWithO", {
        change: reason,
      });
    }
    return lines;
  }, [reviewedSettings, byYou, rulesOnScreen]);
  // The parent builds a fresh rules object during render. Keep the callback
  // payload stable when its visible marks did not change.
  const marksCache = useRef<Readonly<Record<string, string>>>({});
  if (!isSameValue(marksCache.current, marksNow)) marksCache.current = marksNow;
  const marks = marksCache.current;

  const cardState = (key: string, patch: CopilotPatch): CardState => {
    onScreen ??= current();
    if (holds(onScreen, patch.ops)) return "on";
    return made[key] ? "again" : "offered";
  };

  const withBand = (choice: CopilotChoice): CopilotChoice => {
    if (choice.id !== TALL_CHOICE) return choice;
    onScreen ??= current();
    if (!hasTallBand(onScreen)) return choice;
    const writesBand = choice.options.findIndex((option) =>
      option.patch?.ops.some(({ path, to }) => path.startsWith(`${BANDS}.`) && to != null),
    );
    const band =
      writesBand >= 0 ? writesBand : choice.options.findIndex(({ selected }) => selected);
    if (band < 0) return choice;
    return {
      ...choice,
      options: choice.options.map((option, index) => ({
        ...option,
        selected: index === band,
      })),
    };
  };

  const patchOfPick = (pick: CopilotPick): CopilotPatch | undefined => {
    if (pick.kind === "fix")
      return pick.patch ?? findings.find(({ id }) => id === pick.finding)?.fix;
    if (pick.kind !== "option") return undefined;
    return choices
      .find(({ id }) => id === pick.choice)
      ?.options.find((option) => option.patch?.id === pick.option)?.patch;
  };

  const storeOpenedBy = (pick: CopilotPick): OpenedStore | null => {
    const id =
      pick.kind === "move"
        ? pick.id
        : pick.kind === "fix"
          ? (
              pick.patch ??
              [...findings, ...(runDetails?.findings ?? [])].find(({ id }) => id === pick.finding)
                ?.fix
            )?.id
          : undefined;
    if (!id?.startsWith(OPEN_STORE)) return null;
    const store = id.slice(OPEN_STORE.length);
    const row = runDetails?.readout?.stores.find(
      (one) => one.store.toLowerCase() === store.toLowerCase(),
    );
    return row ? { store: row.store, storePogId: row.storePogId } : { store };
  };

  const shownRecommendation = (() => {
    if (!recommendation || usedChips.includes(chipKey(recommendation))) return null;
    const patch = recommendation.pick && patchOfPick(recommendation.pick);
    onScreen ??= current();
    return patch && holds(onScreen, patch.ops) ? null : recommendation;
  })();

  // Keep the previous option tappable while the optimistic option is awaiting its response.

  const shownChoices = choices
    .map((choice) => {
      const tapped = taps.current[choice.id];
      if (!(choice.id in flips) || !tapped) return choice;
      const back = { ...tapped.patch, ops: tapped.patch.ops.map((op) => ({ ...op, to: op.from })) };
      return {
        ...choice,
        options: choice.options.map((option) => ({
          ...option,
          selected: option.label === flips[choice.id],
          patch: option.label === tapped.from ? (option.patch ?? back) : option.patch,
        })),
      };
    })
    .map(withBand);

  return {
    isReady,
    briefFailed,

    isAsking: asking > 0,
    isThinking: thinking > 0,
    trouble,
    headline,
    choices: shownChoices,

    idle,
    alwaysOn,
    findings,

    suggestions: suggestions.filter((chip) => {
      const key = chipKey(chip);
      return key !== (recommendation && chipKey(recommendation)) && !usedChips.includes(key);
    }),
    recommendation: shownRecommendation,

    observations,
    concierge,
    conciergeReady,
    setup: setupRows,
    setupApplied,
    kept,
    // The recommendation hides while this question is asked, so its chip stays a choice here.
    asks: asks.filter((chip) => !usedChips.includes(chipKey(chip))),
    runState,
    isWording: isWording && wording.current !== null && isUntouched(wording.current),
    question,
    openingEntry,
    palette,
    setupStateOf,
    undoSetup,
    setupAgain,

    tapRecommendation: (chip: CopilotChip) =>
      setUsedChips((all) => (all.includes(chipKey(chip)) ? all : [...all, chipKey(chip)])),
    isAdjustAsked,

    changes,

    marks,
    hasLastRunRules,
    copyCompatibility,
    settings: settingRows.current,
    storeLayoutStrategy: rulesOnScreen.store_layout_strategy,
    reviewedSettings: reviewedSettings.filter(({ path, value }) =>
      isSameValue(valueAt(rulesOnScreen, path), value),
    ),
    lastRun,
    runDetails,
    isDetailsLoading,
    entries,
    cardState,

    leaveForAdvanced: () => {
      if (concierge) setConciergeReady(false);
      beforeAdvanced.current = current();
    },

    backFromAdvanced: () => {
      const before = beforeAdvanced.current;
      beforeAdvanced.current = null;
      const paths = before ? changedPaths(before, current()) : [];
      if (paths.length > 0) {
        isEditedInAdvanced.current = true;
        add({ role: "copilot", blocks: [{ type: "text", text: advancedLine(paths) }] });
        reviewed.current = reviewed.current.filter(
          ({ path }) => !paths.some((changed) => overlaps(path, changed)),
        );
        setReviewedSettings(reviewed.current);
        setByYou((all) => [...new Set([...all, ...paths])]);
        outdateSent();
      }
      if (isReady) refreshAfter();
    },
    ask,
    pickOption,
    fix: makeFix,
    storeOpenedBy,

    use: (key: string, patch: CopilotPatch) => {
      writeChange(patch.ops, patch);
      setMade((all) => ({ ...all, [key]: true }));
    },
    undo: (key: string, patch: CopilotPatch) => {
      // Only this panel's own undo and redo cards reverse a reversal.
      const action = journalActions.current.get(patch);
      if (moveJournal(action?.patch ?? patch, action?.undoing ?? true)) {
        outdateSent();
      }
      setTrouble(null);
      refreshAfter();
      setMade((all) => ({ ...all, [key]: false }));
    },

    undoChange,

    backToStart: () => {
      const start = lastRunRules.current ?? oSetup.current;
      if (!start) return;

      const restored = changes.map(
        (row) => `${row.label}: ${row.fromText.charAt(0).toLowerCase()}${row.fromText.slice(1)}`,
      );
      if (restored.length > 0) {
        add({
          role: "copilot",
          blocks: [
            {
              type: "text",
              text: t(
                lastRunRules.current ? "configCopilot.backOnLastRun" : "configCopilot.backOnSetup",
                { rows: restored.join("; ") },
              ),
            },
          ],
        });
      }
      const rules = structuredClone(start);
      const before = current();
      rememberPatch(
        pagePatch(
          "back_to_start",
          "Reset rules",
          changedPaths(before, rules).map((path) => ({
            path,
            label: path,
            from: valueAt(before, path) ?? null,
            to: valueAt(rules, path) ?? null,
          })),
        ),
        before,
        rules,
      );
      put(rules);
      outdateSent();
      setChanges([]);
      setTrouble(null);
      refreshAfter();
    },
    toggleRunDetails,
    retry: () => void brief(),
  };
};
